import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { TbPencil } from 'react-icons/tb';
import { BiCheck } from 'react-icons/bi';
import ReactCrop, { convertToPixelCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';

import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { Form as FinalForm } from 'react-final-form';
import * as validators from '../../util/validators';
import { ProfileSettingsModal, FieldTextInput } from '../../components';

import css from './ProfileSettingsProfileTitleForm.css';

const ACCEPT_IMAGES = 'image/*';
const CROP_ASPECT = 1;

function getCenteredAspectCrop(mediaWidth, mediaHeight, aspect) {
  let cropWidth;
  let cropHeight;
  if (mediaWidth / mediaHeight > aspect) {
    cropHeight = mediaHeight;
    cropWidth = cropHeight * aspect;
  } else {
    cropWidth = mediaWidth;
    cropHeight = cropWidth / aspect;
  }
  const x = ((mediaWidth - cropWidth) / 2 / mediaWidth) * 100;
  const y = ((mediaHeight - cropHeight) / 2 / mediaHeight) * 100;
  const width = (cropWidth / mediaWidth) * 100;
  const height = (cropHeight / mediaHeight) * 100;
  return { unit: '%', x, y, width, height, aspect };
}

function canvasPreview(image, canvas, crop) {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;
  const pixelRatio = window.devicePixelRatio || 1;

  canvas.width = Math.floor(crop.width * scaleX * pixelRatio);
  canvas.height = Math.floor(crop.height * scaleY * pixelRatio);

  ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
  ctx.imageSmoothingQuality = 'high';

  ctx.drawImage(
    image,
    crop.x * scaleX,
    crop.y * scaleY,
    crop.width * scaleX,
    crop.height * scaleY,
    0,
    0,
    crop.width * scaleX,
    crop.height * scaleY
  );
}

const ProfileSettingsProfileTitleForm = props => {
  const {
    isOpen,
    onClose,
    onManageDisableScrolling,
    onSubmit,
    initialValues,
    updateInProgress,
    uploadInProgress,
    profileImagePreview,
    onImageUpload,
    isCustomer,
    intl,
  } = props;

  const fileInputRef = useRef(null);
  const imgRef = useRef(null);
  const previewCanvasRef = useRef(null);
  const imageReaderRef = useRef(null);

  const [cropSrc, setCropSrc] = useState(null);
  const [crop, setCrop] = useState(null);
  const [completedCrop, setCompletedCrop] = useState(null);

  // FileReader is only available in the browser
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const reader = new FileReader();
    reader.addEventListener('load', () => setCropSrc(reader.result));
    imageReaderRef.current = reader;
    return () => {
      imageReaderRef.current = null;
    };
  }, []);

  // Reset crop state whenever the modal is closed
  useEffect(() => {
    if (!isOpen) {
      setCropSrc(null);
      setCrop(null);
      setCompletedCrop(null);
    }
  }, [isOpen]);

  // Keep the canvas preview in sync so we can generate a blob on confirm
  useEffect(() => {
    if (
      completedCrop?.width &&
      completedCrop?.height &&
      imgRef.current &&
      previewCanvasRef.current
    ) {
      canvasPreview(imgRef.current, previewCanvasRef.current, completedCrop);
    }
  }, [completedCrop]);

  const handleFileChange = e => {
    const file = e.target.files && e.target.files[0];
    if (!file || !imageReaderRef.current) return;
    imageReaderRef.current.readAsDataURL(file);
  };

  const handleCropImageLoad = e => {
    const { width, height } = e.currentTarget;
    const centered = getCenteredAspectCrop(width, height, CROP_ASPECT);
    setCrop(centered);
    setCompletedCrop(convertToPixelCrop(centered, width, height));
  };

  const handleUseImage = useCallback(() => {
    const canvas = previewCanvasRef.current;
    if (!canvas || !completedCrop) return;
    canvas.toBlob(
      blob => {
        if (!blob) return;
        const tempId = `avatar_${Date.now()}`;
        blob.name = tempId;
        const result = onImageUpload({ id: tempId, file: blob });
        // If the thunk returns a promise, wait before closing the crop view;
        // otherwise close immediately.
        Promise.resolve(result).finally(() => {
          setCropSrc(null);
          setCrop(null);
          setCompletedCrop(null);
        });
      },
      'image/jpeg',
      1
    );
  }, [completedCrop, onImageUpload]);

  const handleCancelCrop = () => {
    setCropSrc(null);
    setCrop(null);
    setCompletedCrop(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const openFilePicker = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const firstNameLabel = intl.formatMessage({ id: 'ProfileSettingsForm.firstNameLabel' });
  const lastNameLabel = intl.formatMessage({ id: 'ProfileSettingsForm.lastNameLabel' });
  const profileTitleLabel = intl.formatMessage({ id: 'ProfileSettingsPage.profileTitleLabel' });
  const nameTooShortMessage = intl.formatMessage(
    { id: 'SignupForm.nameTooShort' },
    { minLength: validators.NAME_MIN_LENGTH }
  );
  const nameRestrictedMessage = intl.formatMessage({ id: 'SignupForm.nameRestricted' });
  const nameInvalidCharactersMessage = intl.formatMessage({
    id: 'SignupForm.nameInvalidCharacters',
  });
  const nameMinLength = validators.minLength(nameTooShortMessage, validators.NAME_MIN_LENGTH);
  const nameNotRestricted = validators.nameRestricted(
    nameRestrictedMessage,
    validators.RESTRICTED_NAME_TERMS
  );
  const nameCharsValid = validators.validName(nameInvalidCharactersMessage);

  const firstNameValidators = validators.composeValidators(
    validators.required(intl.formatMessage({ id: 'ProfileSettingsForm.firstNameRequired' })),
    nameMinLength,
    nameNotRestricted,
    nameCharsValid
  );
  const lastNameValidators = validators.composeValidators(
    validators.required(intl.formatMessage({ id: 'ProfileSettingsForm.lastNameRequired' })),
    nameMinLength,
    nameNotRestricted,
    nameCharsValid
  );

  const isCropping = !!cropSrc;

  return (
    <FinalForm
      key={isOpen}
      onSubmit={values => {
        onSubmit({
          firstName: (values.firstName || '').trim(),
          lastName: (values.lastName || '').trim(),
          whoAmI: values.whoAmI || '',
        });
        onClose();
      }}
      initialValues={{
        firstName: initialValues?.firstName || '',
        lastName: initialValues?.lastName || '',
        whoAmI: initialValues?.whoAmI || '',
      }}
      render={({ handleSubmit, dirty, invalid }) => {
        const imageDirty = !!initialValues?.imageDirty;
        const canSubmit = (dirty || imageDirty) && !invalid;
        const submitDisabled = !canSubmit || uploadInProgress || isCropping;

        return (
          <ProfileSettingsModal
            id="profileHeader"
            isOpen={isOpen}
            onClose={onClose}
            onManageDisableScrolling={onManageDisableScrolling}
            title={intl.formatMessage({ id: 'ProfileSettingsPage.editProfileHeaderTitle' })}
            subtitle={intl.formatMessage({ id: 'ProfileSettingsPage.editProfileHeaderSubtitle' })}
            onSubmit={handleSubmit}
            submitDisabled={submitDisabled}
            submitInProgress={updateInProgress}
            submitLabel={intl.formatMessage({ id: 'ProfileSettingsPage.saveButton' })}
          >
            {isCropping ? (
              <div className={css.cropBlock}>
                <div className={css.cropWrapper}>
                  <ReactCrop
                    crop={crop}
                    aspect={CROP_ASPECT}
                    onChange={(_, percentCrop) => setCrop(percentCrop)}
                    onComplete={c => setCompletedCrop(c)}
                    keepSelection
                    circularCrop
                  >
                    <img
                      ref={imgRef}
                      src={cropSrc}
                      alt=""
                      onLoad={handleCropImageLoad}
                      className={css.cropImage}
                    />
                  </ReactCrop>
                  <canvas ref={previewCanvasRef} className={css.hiddenCanvas} />
                </div>
                <div className={css.cropActions}>
                  <button
                    type="button"
                    className={css.secondaryButton}
                    onClick={handleCancelCrop}
                    disabled={uploadInProgress}
                  >
                    {intl.formatMessage({ id: 'ProfileSettingsPage.cropCancel' })}
                  </button>
                  <button
                    type="button"
                    className={css.primaryButton}
                    onClick={handleUseImage}
                    disabled={!completedCrop || uploadInProgress}
                  >
                    {uploadInProgress
                      ? intl.formatMessage({ id: 'ProfileSettingsPage.cropUploading' })
                      : intl.formatMessage({ id: 'ProfileSettingsPage.cropUseImage' })}
                  </button>
                </div>
              </div>
            ) : (
              <div className={css.imageRow}>
                <button
                  type="button"
                  className={css.avatarButton}
                  onClick={openFilePicker}
                  aria-label={intl.formatMessage({ id: 'ProfileSettingsPage.profileImageLabel' })}
                >
                  <div className={css.avatarStack}>{profileImagePreview}</div>
                  <span className={css.avatarEditPen}>
                    <TbPencil className={css.avatarEditPenIcon} />
                  </span>
                </button>
                <div className={css.imageHint}>
                  <p className={css.imageHintTitle}>
                    {intl.formatMessage({ id: 'ProfileSettingsPage.profileImageLabel' })}{' '}
                    <span className={css.imageHintOptional}>
                      {intl.formatMessage({ id: 'ProfileSettingsPage.profileImageOptional' })}
                    </span>
                  </p>
                  <ul className={css.imageHintList}>
                    <li className={css.imageHintItem}>
                      <BiCheck className={css.imageHintCheck} />
                      <span>
                        {intl.formatMessage({
                          id: 'ProfileSettingsPage.profileImageHintNoLogos',
                        })}
                      </span>
                    </li>
                    <li className={css.imageHintItem}>
                      <BiCheck className={css.imageHintCheck} />
                      <span>
                        {intl.formatMessage({ id: 'ProfileSettingsPage.profileImageHint' })}
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPT_IMAGES}
              className={css.hiddenFileInput}
              onChange={handleFileChange}
              onClick={e => {
                e.target.value = null;
              }}
            />

            <div className={css.nameRow}>
              <FieldTextInput
                className={css.nameField}
                name="firstName"
                id="firstName"
                type="text"
                label={firstNameLabel}
                validate={firstNameValidators}
                showSpacer={false}
              />
              <FieldTextInput
                className={css.nameField}
                name="lastName"
                id="lastName"
                type="text"
                label={lastNameLabel}
                validate={lastNameValidators}
                showSpacer={false}
              />
            </div>

            <FieldTextInput
              name="whoAmI"
              id="whoAmI"
              type="text"
              label={profileTitleLabel}
              showSpacer={false}
            />

            <div className={css.infoBox}>
              <div className={css.infoBoxHeader}>
                <span className={css.infoBoxIcon}>?</span>
                <p className={css.infoBoxTitle}>
                  {intl.formatMessage({ id: 'ProfileSettingsPage.profileTitleInfoTitle' })}
                </p>
              </div>
              <p className={css.infoBoxBody}>
                <FormattedMessage
                  id={
                    isCustomer
                      ? 'ProfileSettingsPage.profileTitleInfoBodyCustomer'
                      : 'ProfileSettingsPage.profileTitleInfoBody'
                  }
                  values={{ b: chunks => <strong className={css.infoBoxBold}>{chunks}</strong> }}
                />
              </p>
            </div>
          </ProfileSettingsModal>
        );
      }}
    />
  );
};

ProfileSettingsProfileTitleForm.defaultProps = {
  initialValues: {},
  updateInProgress: false,
  uploadInProgress: false,
  profileImagePreview: null,
  onImageUpload: () => {},
  isCustomer: false,
};

ProfileSettingsProfileTitleForm.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onManageDisableScrolling: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  initialValues: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    whoAmI: PropTypes.string,
    imageDirty: PropTypes.bool,
  }),
  updateInProgress: PropTypes.bool,
  uploadInProgress: PropTypes.bool,
  profileImagePreview: PropTypes.node,
  onImageUpload: PropTypes.func,
  isCustomer: PropTypes.bool,
  intl: intlShape.isRequired,
};

export default injectIntl(ProfileSettingsProfileTitleForm);
